<script setup lang="ts">
/**
 * The toolbar of a rich-text editor — one look for every backend (I-2799):
 * Write's canvas for `.writez` and the contenteditable HTML editor for HTML.
 * It only shows the `state` the editor reports and emits `command`; the editor
 * behind it does the work. A command the editor cannot run is listed in
 * `disabledCommands` and stays visible, greyed out.
 *
 * Two layouts. `compact` is one row of the tools used most — undo, paragraph
 * style, font and size, bold/italic/underline and colours, alignment and lists,
 * link/picture/table — and everything else under „⋯“; a host picks which of
 * those groups stand in the row (`groups`), the rest moves under „⋯“ too.
 * `full` shows every tool in two rows. The host's own tools sit at the end of
 * the row (slot `end`), its own commands under „⋯“ (`extraActions`) or as
 * buttons after the insert tools (slot `insert-actions`). Extensions
 * (`extensions.ts`) are buttons after the insert tools whose panel opens
 * beside the button; the editor hands their context in (`extensionContext`)
 * and opens one from its shortcut (`openExtension`).
 */
import { computed, inject, nextTick, onBeforeUnmount, ref, shallowRef, watch } from 'vue';
import { NxIconButton } from '../IconButton';
import { NxOfficeIcon } from '../OfficeRibbon';
import type { NxOfficeIconName } from '../OfficeRibbon';
import { NxPopover } from '../Popover';
import { NxTooltip } from '../Tooltip';
import { i18nInjectionKey } from '../internal';
import { normalizeRichTextHref } from './href';
import RichTextLinkForm from './RichTextLinkForm.vue';
import NxTableSizeGrid from './NxTableSizeGrid.vue';
import type { NxRichTextExtension, NxRichTextExtensionContext } from './extensions';
import NxRichTextExtensionButtons from './NxRichTextExtensionButtons.vue';
import { NX_RICH_TEXT_TOOLBAR_DEFAULT_GROUPS } from './types';
import type {
  NxRichTextAlign,
  NxRichTextCommand,
  NxRichTextCommandId,
  NxRichTextExtraAction,
  NxRichTextFontFamily,
  NxRichTextImageWrap,
  NxRichTextMark,
  NxRichTextPasteMode,
  NxRichTextToolbarGroup,
  NxRichTextToolbarLayout,
  NxRichTextToolbarState,
} from './types';

const props = withDefaults(
  defineProps<{
    state: NxRichTextToolbarState;
    fontFamilies: readonly NxRichTextFontFamily[];
    fontSizes: readonly number[];
    /** Line spacings in percent (100 = single). */
    lineSpacings: readonly number[];
    /** Commands the editor cannot run: shown, but disabled. */
    disabledCommands?: readonly NxRichTextCommandId[];
    allowImages?: boolean;
    /** Block content — tables, quotes, dividers. Off for one-paragraph fields. */
    blocks?: boolean;
    layout?: NxRichTextToolbarLayout;
    /** Which groups the compact row shows; unset ones follow the defaults. */
    groups?: Partial<Record<NxRichTextToolbarGroup, boolean>>;
    /** The host's own commands under „⋯“; picking one emits `extra`. */
    extraActions?: readonly NxRichTextExtraAction[];
    /** Offer „Customise toolbar…“ under „⋯“; picking it emits `customize`. */
    customizable?: boolean;
    /** Extensions the editor offers: a button each, after the insert tools. */
    extensions?: readonly NxRichTextExtension[];
    /** What an extension's panel gets, read when it opens (format, selection, …). */
    extensionContext?: () => Omit<NxRichTextExtensionContext, 'close'>;
  }>(),
  {
    disabledCommands: () => [],
    allowImages: true,
    blocks: true,
    layout: 'compact',
    groups: () => ({}),
    extraActions: () => [],
    customizable: false,
    extensions: () => [],
    extensionContext: undefined,
  }
);

const emit = defineEmits<{
  command: [command: NxRichTextCommand];
  extra: [id: string];
  customize: [];
  /** An extension's panel closed itself; the editor puts the caret back. */
  'extension-closed': [];
}>();

/** Whether the link editor is open; a host binds it to open it from Ctrl/Cmd+K. */
const linkOpen = defineModel<boolean>('linkOpen', { default: false });
const tableOpen = defineModel<boolean>('tableOpen', { default: false });
/** The address typed into the link editor. */
const linkHref = defineModel<string>('linkHref', { default: '' });

const i18n = inject(i18nInjectionKey, null);
function tr(key: string, fallback: string, params?: Record<string, unknown>): string {
  const full = `common.rich_text.${key}`;
  const text = i18n?.t(full, params);
  if (text && text !== full) return text;
  return params
    ? fallback.replace(/\{(\w+)\}/g, (_, name: string) => String(params[name] ?? ''))
    : fallback;
}

const mac =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform ?? '');
/** A shortcut as the platform writes it: ⌘B on a Mac, Ctrl+B elsewhere. */
function keys(key: string, shift = false): string {
  if (mac) return `${shift ? '⇧' : ''}⌘${key}`;
  return [tr('key_ctrl', 'Ctrl'), shift ? tr('key_shift', 'Shift') : '', key]
    .filter(Boolean)
    .join('+');
}
const titled = (label: string, shortcut?: string) => (shortcut ? `${label} (${shortcut})` : label);

const full = computed(() => props.layout === 'full');
/** Whether a group stands in the row; the full layout shows all of them. */
function inBar(group: NxRichTextToolbarGroup): boolean {
  return full.value || (props.groups[group] ?? NX_RICH_TEXT_TOOLBAR_DEFAULT_GROUPS[group]);
}

const disabled = computed(() => new Set(props.disabledCommands));
const off = (id: NxRichTextCommandId) => disabled.value.has(id);

function run(command: NxRichTextCommand): void {
  if (off(command.id)) return;
  emit('command', command);
}

const MARKS: readonly [NxRichTextMark, NxOfficeIconName, string, string, string?][] = [
  ['bold', 'bold', 'bold', 'Bold', 'B'],
  ['italic', 'italic', 'italic', 'Italic', 'I'],
  ['underline', 'underline', 'underline', 'Underline', 'U'],
];
const MORE_MARKS: readonly [NxRichTextMark, NxOfficeIconName, string, string][] = [
  ['strike', 'strikethrough', 'strikethrough', 'Strikethrough'],
  ['sup', 'superscript', 'superscript', 'Superscript'],
  ['sub', 'subscript', 'subscript', 'Subscript'],
];
const ALIGNS: readonly [NxRichTextAlign, NxOfficeIconName, string, string, string][] = [
  ['left', 'align-left', 'align_left', 'Align left', 'L'],
  ['center', 'align-center', 'align_center', 'Align center', 'E'],
  ['right', 'align-right', 'align_right', 'Align right', 'R'],
  ['justify', 'align-justify', 'justify', 'Justify', 'J'],
];
const IMAGE_WRAPS: readonly [NxRichTextImageWrap, string, string][] = [
  ['inline', 'wrap_inline', 'In line'],
  ['left', 'wrap_left', 'Wrap left'],
  ['right', 'wrap_right', 'Wrap right'],
];
const PASTE_MODES: readonly [NxRichTextPasteMode, string, string][] = [
  ['ask', 'paste_ask', 'Ask when pasting from Word'],
  ['source', 'paste_source', 'Keep source formatting'],
  ['destination', 'paste_destination', 'Match destination'],
  ['text', 'paste_text', 'Plain text'],
];

const alignIcon = computed<NxOfficeIconName>(
  () => ALIGNS.find(([align]) => align === props.state.align)?.[1] ?? 'align-left'
);

// A family or size the document brings along (Calibri from Outlook) is shown as it is.
const familyOptions = computed(() =>
  !props.state.fontFamily ||
  props.fontFamilies.some((family) => family.value === props.state.fontFamily)
    ? props.fontFamilies
    : [...props.fontFamilies, { value: props.state.fontFamily, label: props.state.fontFamily }]
);
const sizeOptions = computed(() =>
  !props.state.fontSize || props.fontSizes.includes(props.state.fontSize)
    ? props.fontSizes
    : [...props.fontSizes, props.state.fontSize].sort((a, b) => a - b)
);
const headingOptions = computed(() => [
  { value: '0', label: tr('normal', 'Normal') },
  ...[1, 2, 3].map((level) => ({
    value: String(level),
    label: tr(`heading_${level}`, `Heading ${level}`),
  })),
]);
const spacingOptions = computed(() => [
  { value: '', label: tr('default', 'Default') },
  ...props.lineSpacings.map((spacing) => ({
    value: String(spacing),
    label: String(spacing / 100),
  })),
]);
const pasteOptions = computed(() =>
  PASTE_MODES.map(([mode, key, label]) => ({ value: mode, label: tr(key, label) }))
);
/** Under „⋯“ the label says „Pasting from Word“ already: the choices say only what happens. */
const PASTE_CHOICES: Record<NxRichTextPasteMode, string> = {
  ask: 'Ask',
  source: 'Keep formatting',
  destination: 'Match the text',
  text: 'Plain text',
};
const pasteChoices = computed(() =>
  PASTE_MODES.map(([mode]) => ({
    value: mode,
    label: tr(`paste_choice_${mode}`, PASTE_CHOICES[mode]),
  }))
);

function headingCommand(value: string): NxRichTextCommand {
  const level = Number(value);
  return { id: 'heading', level: level >= 1 && level <= 3 ? level : 0 };
}
function lineSpacingCommand(value: string): NxRichTextCommand {
  const spacing = Number(value);
  return { id: 'lineSpacing', spacing: props.lineSpacings.includes(spacing) ? spacing : null };
}
const pick = (event: Event) => (event.target as HTMLSelectElement | HTMLInputElement).value;

// ---- Popovers: link, table, alignment, „⋯“ ----

const menuOpen = ref(false);
const alignOpen = ref(false);
const insertInBar = computed(() => inBar('insert'));
/** Link and table open in the row when the insert tools are there, else under „⋯“. */
const barLinkOpen = computed({
  get: () => linkOpen.value && insertInBar.value,
  set: (open: boolean) => (linkOpen.value = open),
});
const barTableOpen = computed({
  get: () => tableOpen.value && insertInBar.value,
  set: (open: boolean) => (tableOpen.value = open),
});
const menuView = computed<'main' | 'link' | 'table'>(() => {
  if (insertInBar.value) return 'main';
  if (linkOpen.value) return 'link';
  if (tableOpen.value) return 'table';
  return 'main';
});
const menuShown = computed({
  get: () => menuOpen.value || menuView.value !== 'main',
  set: (open: boolean) => {
    menuOpen.value = open;
    if (!open && !insertInBar.value) {
      linkOpen.value = false;
      tableOpen.value = false;
    }
  },
});

const barLinkForm = ref<InstanceType<typeof RichTextLinkForm> | null>(null);
const menuLinkForm = ref<InstanceType<typeof RichTextLinkForm> | null>(null);
function focusLink(): void {
  void nextTick(() => (insertInBar.value ? barLinkForm : menuLinkForm).value?.focus());
}

watch(linkOpen, (open) => {
  if (!open) return;
  tableOpen.value = false;
  alignOpen.value = false;
  linkHref.value = props.state.linkHref ?? '';
});
watch(tableOpen, (open) => {
  if (!open) return;
  linkOpen.value = false;
  alignOpen.value = false;
});
watch(menuView, (view) => {
  if (view === 'link') focusLink();
});
// Ctrl/Cmd+K with the insert tools under „⋯“ opens the menu and its link form
// at once: the form is still hidden when the watcher above runs, so it takes
// the focus once the menu is shown.
function onMenuOpened(): void {
  if (menuView.value === 'link') focusLink();
}

function applyLink(): void {
  const raw = linkHref.value.trim();
  if (!raw) {
    removeLink();
    return;
  }
  const href = normalizeRichTextHref(raw);
  if (!href) return;
  run({ id: 'link', href });
  closeLink();
}
function removeLink(): void {
  run({ id: 'unlink' });
  closeLink();
}
function closeLink(): void {
  linkOpen.value = false;
  menuOpen.value = false;
}
function pickTable(size: { rows: number; columns: number }): void {
  run({ id: 'table', ...size });
  tableOpen.value = false;
  menuOpen.value = false;
}
function pickAlign(align: NxRichTextAlign): void {
  run({ id: 'align', align });
  alignOpen.value = false;
}
const tableDimensions = (size: { rows: number; columns: number }) =>
  tr('table_dimensions', '{rows} by {columns} table', size);

// ---- „⋯“: what the row does not show ----

interface MenuAction {
  kind: 'action';
  key: string;
  label: string;
  icon?: NxOfficeIconName;
  shortcut?: string;
  pressed?: boolean;
  disabled?: boolean;
  /** Stays open: the entry opens a view of the menu (link, table). */
  keepOpen?: boolean;
  select: () => void;
}
interface MenuSelect {
  kind: 'select';
  key: string;
  label: string;
  value: string;
  options: readonly { value: string; label: string }[];
  disabled?: boolean;
  change: (value: string) => void;
}
interface MenuColor {
  kind: 'color';
  key: string;
  label: string;
  icon: NxOfficeIconName;
  value: string;
  current?: string;
  disabled?: boolean;
  change: (value: string) => void;
}
type MenuEntry = MenuAction | MenuSelect | MenuColor;

function action(
  key: string,
  icon: NxOfficeIconName | undefined,
  label: string,
  command: NxRichTextCommand,
  extra: Partial<Pick<MenuAction, 'shortcut' | 'pressed' | 'disabled'>> = {}
): MenuAction {
  return {
    kind: 'action',
    key,
    icon,
    label,
    ...extra,
    disabled: !!extra.disabled || off(command.id),
    select: () => run(command),
  };
}

const menuSections = computed<{ key: string; label: string; entries: MenuEntry[] }[]>(() => {
  const s = props.state;
  const compact = !full.value;
  const edit: MenuEntry[] = [];
  if (!inBar('history')) {
    edit.push(
      action(
        'undo',
        'undo',
        tr('undo', 'Undo'),
        { id: 'undo' },
        {
          shortcut: keys('Z'),
          disabled: !s.canUndo,
        }
      ),
      action(
        'redo',
        'redo',
        tr('redo', 'Redo'),
        { id: 'redo' },
        {
          shortcut: keys('Z', true),
          disabled: !s.canRedo,
        }
      )
    );
  }
  edit.push(
    action(
      'cut',
      'cut',
      tr('cut', 'Cut'),
      { id: 'cut' },
      {
        shortcut: keys('X'),
        disabled: !s.hasSelection,
      }
    ),
    action(
      'copy',
      'copy',
      tr('copy', 'Copy'),
      { id: 'copy' },
      {
        shortcut: keys('C'),
        disabled: !s.hasSelection,
      }
    ),
    action('paste', 'paste', tr('paste', 'Paste'), { id: 'paste' }, { shortcut: keys('V') })
  );

  const text: MenuEntry[] = [];
  if (!inBar('font')) {
    text.push(
      {
        kind: 'select',
        key: 'heading',
        label: tr('paragraph_style', 'Paragraph style'),
        value: String(s.heading),
        options: headingOptions.value,
        disabled: off('heading'),
        change: (value) => run(headingCommand(value)),
      },
      {
        kind: 'select',
        key: 'family',
        label: tr('font', 'Font'),
        value: s.fontFamily,
        options: familyOptions.value,
        disabled: off('fontFamily'),
        change: (family) => run({ id: 'fontFamily', family }),
      },
      {
        kind: 'select',
        key: 'size',
        label: tr('font_size', 'Font size'),
        value: String(s.fontSize),
        options: sizeOptions.value.map((size) => ({ value: String(size), label: String(size) })),
        disabled: off('fontSize'),
        change: (size) => run({ id: 'fontSize', size: Number(size) }),
      }
    );
  }
  if (!inBar('marks')) {
    for (const [mark, icon, key, label, shortcut] of MARKS) {
      text.push(
        action(
          mark,
          icon,
          tr(key, label),
          { id: 'mark', mark },
          {
            shortcut: shortcut && keys(shortcut),
            pressed: !!s.marks[mark],
          }
        )
      );
    }
    text.push(
      {
        kind: 'color',
        key: 'color',
        icon: 'font-color',
        label: tr('text_color', 'Text color'),
        value: s.color,
        current: s.explicitColor,
        disabled: off('color'),
        change: (color) => run({ id: 'color', color: color.toUpperCase() }),
      },
      {
        kind: 'color',
        key: 'highlight',
        icon: 'highlight',
        label: tr('highlight_color', 'Highlight color'),
        value: s.highlight,
        disabled: off('highlight'),
        change: (color) => run({ id: 'highlight', color: color.toUpperCase() }),
      }
    );
  }
  if (compact) {
    for (const [mark, icon, key, label] of MORE_MARKS) {
      text.push(
        action(mark, icon, tr(key, label), { id: 'mark', mark }, { pressed: !!s.marks[mark] })
      );
    }
    text.push(
      action('clear', 'clear-formatting', tr('clear_formatting', 'Clear formatting'), {
        id: 'clearFormatting',
      })
    );
  }

  const paragraph: MenuEntry[] = [];
  if (!inBar('paragraph')) {
    for (const [align, icon, key, label, shortcut] of ALIGNS) {
      paragraph.push(
        action(
          align,
          icon,
          tr(key, label),
          { id: 'align', align },
          {
            shortcut: keys(shortcut),
            pressed: s.align === align,
          }
        )
      );
    }
    paragraph.push(
      action(
        'bullets',
        'bullets',
        tr('bullet_list', 'Bullet list'),
        {
          id: 'list',
          list: 'bulletList',
        },
        { pressed: s.list === 'bulletList' }
      ),
      action(
        'numbers',
        'numbering',
        tr('numbered_list', 'Numbered list'),
        {
          id: 'list',
          list: 'orderedList',
        },
        { pressed: s.list === 'orderedList' }
      )
    );
  }
  if (compact) {
    paragraph.push(
      action(
        'indent',
        'indent-increase',
        tr('increase_indent', 'Increase indent'),
        {
          id: 'indent',
          delta: 1,
        },
        { shortcut: keys('M') }
      ),
      action(
        'outdent',
        'indent-decrease',
        tr('decrease_indent', 'Decrease indent'),
        {
          id: 'indent',
          delta: -1,
        },
        { shortcut: keys('M', true) }
      ),
      {
        kind: 'select',
        key: 'spacing',
        label: tr('line_spacing', 'Line spacing'),
        value: String(s.lineSpacing ?? ''),
        options: spacingOptions.value,
        disabled: off('lineSpacing'),
        change: (value) => run(lineSpacingCommand(value)),
      }
    );
  }
  if (props.blocks) {
    if (compact)
      paragraph.push(action('quote', 'quote', tr('add_quote', 'Quote'), { id: 'quote', delta: 1 }));
    paragraph.push(
      action('unquote', undefined, tr('remove_quote', 'Remove quote'), { id: 'quote', delta: -1 })
    );
    if (compact)
      paragraph.push(action('divider', 'divider', tr('divider', 'Divider'), { id: 'divider' }));
  }

  const insert: MenuEntry[] = [];
  if (!insertInBar.value) {
    insert.push({
      kind: 'action',
      key: 'link',
      icon: 'link',
      label: `${tr('link', 'Link')} …`,
      shortcut: keys('K'),
      pressed: !!s.marks.link,
      disabled: off('link'),
      keepOpen: true,
      select: () => (linkOpen.value = true),
    });
    if (props.allowImages)
      insert.push(action('image', 'picture', tr('image', 'Image'), { id: 'image' }));
    if (props.blocks) {
      insert.push({
        kind: 'action',
        key: 'table',
        icon: 'table',
        label: `${tr('table', 'Table')} …`,
        disabled: off('table'),
        keepOpen: true,
        select: () => (tableOpen.value = true),
      });
    }
  }
  for (const extra of props.extraActions) {
    insert.push({
      kind: 'action',
      key: `extra:${extra.id}`,
      icon: extra.icon,
      label: extra.label,
      shortcut: extra.shortcut,
      disabled: extra.disabled,
      select: () => emit('extra', extra.id),
    });
  }
  if (!inBar('find')) {
    insert.push(
      action('find', 'find', tr('find', 'Find'), { id: 'find' }, { shortcut: keys('F') }),
      action(
        'replace',
        'replace',
        tr('replace', 'Replace'),
        { id: 'replace' },
        {
          shortcut: keys('H'),
        }
      )
    );
  }

  const more: MenuEntry[] = [];
  if (compact) {
    more.push({
      kind: 'select',
      key: 'paste',
      label: tr('paste_from_word', 'Pasting from Word'),
      value: s.pasteMode,
      options: pasteChoices.value,
      disabled: off('pasteMode'),
      change: (mode) => run({ id: 'pasteMode', mode: mode as NxRichTextPasteMode }),
    });
  }
  more.push(
    action(
      'pdf',
      'download-pdf',
      tr('download_pdf', 'Download PDF'),
      { id: 'pdf' },
      {
        disabled: !!s.pdfBusy || !!s.busy,
      }
    )
  );

  return [
    { key: 'edit', label: tr('section_edit', 'Edit'), entries: edit },
    { key: 'text', label: tr('section_text', 'Text'), entries: text },
    { key: 'paragraph', label: tr('paragraph', 'Paragraph'), entries: paragraph },
    { key: 'insert', label: tr('section_insert', 'Insert & find'), entries: insert },
    { key: 'more', label: tr('section_more', 'More'), entries: more },
  ].filter((section) => section.entries.length > 0);
});

function selectEntry(entry: MenuAction): void {
  if (entry.disabled) return;
  entry.select();
  if (!entry.keepOpen) menuOpen.value = false;
}

function customize(): void {
  menuOpen.value = false;
  emit('customize');
}

// ---- Extensions ----

const extensionButtons = ref<InstanceType<typeof NxRichTextExtensionButtons> | null>(null);

/** Opens an extension's panel; false when the toolbar has no such extension. */
function openExtension(id: string): boolean {
  if (!props.extensionContext || !extensionButtons.value) return false;
  linkOpen.value = false;
  tableOpen.value = false;
  alignOpen.value = false;
  menuOpen.value = false;
  return extensionButtons.value.open(id);
}

// ---- Tooltips: one bubble for every tool, moved along as the calendar does ----
// The tools carry their name (and shortcut) as `title`. While the bubble shows
// it, the title waits in `data-nx-tip`, so the browser's own tooltip does not
// come on top; it goes back when the pointer leaves. Tools with a tooltip of
// their own (the extensions, a host's tools) carry no title and are left alone.
const tipAnchor = shallowRef<HTMLElement | null>(null);
const tipText = ref('');
let tipTimer: ReturnType<typeof setTimeout> | undefined;
/** Long enough that sweeping the row does not strobe, short enough to feel free. */
const TIP_DELAY_MS = 300;

function tipTarget(target: EventTarget | null): HTMLElement | null {
  const el = (target as HTMLElement | null)?.closest?.<HTMLElement>('[title], [data-nx-tip]');
  // Menus and panels under the row label their entries themselves.
  if (!el || el.closest('.nx-popover-panel')) return null;
  return el.closest('.nx-rich-text-toolbar') ? el : null;
}

function releaseTip(el: HTMLElement | null): void {
  if (!el?.dataset.nxTip) return;
  if (!el.hasAttribute('title')) el.setAttribute('title', el.dataset.nxTip);
  delete el.dataset.nxTip;
}

function showTip(event: Event): void {
  const el = tipTarget(event.target);
  if (!el || el === tipAnchor.value) return;
  const text = el.getAttribute('title') || el.dataset.nxTip || '';
  if (!text) return;
  el.dataset.nxTip = text;
  el.removeAttribute('title');
  clearTimeout(tipTimer);
  const move = () => {
    releaseTip(tipAnchor.value);
    tipAnchor.value = el;
    tipText.value = text;
  };
  // A bubble already up follows at once; a fresh one waits out the delay.
  if (tipAnchor.value) move();
  else tipTimer = setTimeout(move, TIP_DELAY_MS);
}

function hideTip(event?: Event): void {
  const next = (event as MouseEvent | FocusEvent | undefined)?.relatedTarget ?? null;
  const target = event ? tipTarget(event.target) : null;
  if (next && tipTarget(next)) {
    releaseTip(target !== tipAnchor.value ? target : null);
    return;
  }
  clearTimeout(tipTimer);
  releaseTip(target);
  releaseTip(tipAnchor.value);
  tipAnchor.value = null;
}

/** Keyboard focus names the tool too; a click's focus does not bring the bubble back. */
function showTipOnFocus(event: FocusEvent): void {
  const el = event.target as HTMLElement | null;
  let keyboard = false;
  try {
    keyboard = !!el?.matches(':focus-visible');
  } catch {
    keyboard = false;
  }
  if (keyboard) showTip(event);
}

onBeforeUnmount(() => {
  clearTimeout(tipTimer);
  releaseTip(tipAnchor.value);
});

defineExpose({
  openExtension,
  /** Open the link editor, e.g. from Ctrl/Cmd+K. */
  openLink: () => {
    linkOpen.value = true;
  },
  closePopovers: () => {
    linkOpen.value = false;
    tableOpen.value = false;
    alignOpen.value = false;
    menuOpen.value = false;
    extensionButtons.value?.close();
  },
});
</script>

<template>
  <div
    class="nx-rich-text-toolbar"
    :class="`nx-rich-text-toolbar--${layout}`"
    role="toolbar"
    :aria-label="tr('editor_tools', 'Editor tools')"
    @pointerover="showTip"
    @pointerout="hideTip"
    @pointerdown="hideTip()"
    @focusin="showTipOnFocus"
    @focusout="hideTip"
    @keydown.esc="hideTip()"
  >
    <NxTooltip :anchor="tipAnchor" :open="!!tipAnchor" :text="tipText" />
    <div class="nx-rich-text-toolbar__bar">
      <div v-if="inBar('history')" class="nx-rich-text-toolbar__group is-history">
        <NxIconButton
          height="xs"
          :disabled="!state.canUndo || off('undo')"
          :title="titled(tr('undo', 'Undo'), keys('Z'))"
          @click="run({ id: 'undo' })"
        >
          <NxOfficeIcon name="undo" />
        </NxIconButton>
        <NxIconButton
          height="xs"
          :disabled="!state.canRedo || off('redo')"
          :title="titled(tr('redo', 'Redo'), keys('Z', true))"
          @click="run({ id: 'redo' })"
        >
          <NxOfficeIcon name="redo" />
        </NxIconButton>
      </div>

      <div v-if="inBar('font')" class="nx-rich-text-toolbar__group is-font">
        <span class="nx-rich-text-toolbar__pick is-heading">
          <select
            :value="String(state.heading)"
            :disabled="off('heading')"
            :aria-label="tr('paragraph_style', 'Paragraph style')"
            :title="tr('paragraph_style', 'Paragraph style')"
            @change="run(headingCommand(pick($event)))"
          >
            <option v-for="option in headingOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </span>
        <span class="nx-rich-text-toolbar__pick is-family">
          <select
            :value="state.fontFamily"
            :disabled="off('fontFamily')"
            :aria-label="tr('font', 'Font')"
            :title="tr('font', 'Font')"
            @change="run({ id: 'fontFamily', family: pick($event) })"
          >
            <option v-for="family in familyOptions" :key="family.value" :value="family.value">
              {{ family.label }}
            </option>
          </select>
        </span>
        <span class="nx-rich-text-toolbar__pick is-size">
          <select
            :value="String(state.fontSize)"
            :disabled="off('fontSize')"
            :aria-label="tr('font_size', 'Font size')"
            :title="tr('font_size', 'Font size')"
            @change="run({ id: 'fontSize', size: Number(pick($event)) })"
          >
            <option v-for="size in sizeOptions" :key="size" :value="String(size)">
              {{ size }}
            </option>
          </select>
        </span>
      </div>

      <div v-if="inBar('marks')" class="nx-rich-text-toolbar__group is-marks">
        <NxIconButton
          v-for="[mark, icon, key, label, shortcut] in MARKS"
          :key="mark"
          height="xs"
          :disabled="off('mark')"
          :pressed="!!state.marks[mark]"
          :title="titled(tr(key, label), shortcut && keys(shortcut))"
          @click="run({ id: 'mark', mark })"
        >
          <NxOfficeIcon :name="icon" />
        </NxIconButton>
        <template v-if="full">
          <NxIconButton
            v-for="[mark, icon, key, label] in MORE_MARKS"
            :key="mark"
            height="xs"
            :disabled="off('mark')"
            :pressed="!!state.marks[mark]"
            :title="tr(key, label)"
            @click="run({ id: 'mark', mark })"
          >
            <NxOfficeIcon :name="icon" />
          </NxIconButton>
          <NxIconButton
            height="xs"
            :disabled="off('clearFormatting')"
            :title="tr('clear_formatting', 'Clear formatting')"
            @click="run({ id: 'clearFormatting' })"
          >
            <NxOfficeIcon name="clear-formatting" />
          </NxIconButton>
        </template>
      </div>

      <div v-if="inBar('marks')" class="nx-rich-text-toolbar__group is-colors">
        <label
          class="nx-rich-text-toolbar__color"
          :class="{ 'is-disabled': off('color') }"
          :title="tr('text_color', 'Text color')"
        >
          <NxOfficeIcon name="font-color" :current="state.explicitColor" />
          <span class="nx-rich-text-toolbar__caret" aria-hidden="true" />
          <input
            type="color"
            :value="state.color"
            :disabled="off('color')"
            :aria-label="tr('text_color', 'Text color')"
            @input="run({ id: 'color', color: pick($event).toUpperCase() })"
          />
        </label>
        <label
          class="nx-rich-text-toolbar__color"
          :class="{ 'is-disabled': off('highlight') }"
          :title="tr('highlight_color', 'Highlight color')"
        >
          <NxOfficeIcon name="highlight" />
          <span class="nx-rich-text-toolbar__caret" aria-hidden="true" />
          <input
            type="color"
            :value="state.highlight"
            :disabled="off('highlight')"
            :aria-label="tr('highlight_color', 'Highlight color')"
            @input="run({ id: 'highlight', color: pick($event).toUpperCase() })"
          />
        </label>
        <span v-if="full" class="nx-rich-text-toolbar__pick is-spacing">
          <select
            :value="String(state.lineSpacing ?? '')"
            :disabled="off('lineSpacing')"
            :aria-label="tr('line_spacing', 'Line spacing')"
            :title="tr('line_spacing', 'Line spacing')"
            @change="run(lineSpacingCommand(pick($event)))"
          >
            <option v-for="option in spacingOptions" :key="option.value" :value="option.value">
              {{ option.value ? option.label : tr('line_spacing', 'Line spacing') }}
            </option>
          </select>
        </span>
      </div>

      <div v-if="inBar('paragraph')" class="nx-rich-text-toolbar__group is-paragraph">
        <template v-if="full">
          <NxIconButton
            v-for="[align, icon, key, label, shortcut] in ALIGNS"
            :key="align"
            height="xs"
            :disabled="off('align')"
            :pressed="state.align === align"
            :title="titled(tr(key, label), keys(shortcut))"
            @click="run({ id: 'align', align })"
          >
            <NxOfficeIcon :name="icon" />
          </NxIconButton>
        </template>
        <NxPopover v-else v-model:open="alignOpen" :label="tr('alignment', 'Alignment')">
          <template #trigger>
            <button
              type="button"
              class="nx-rich-text-toolbar__split"
              :disabled="off('align')"
              :title="tr('alignment', 'Alignment')"
              :aria-label="tr('alignment', 'Alignment')"
            >
              <NxOfficeIcon :name="alignIcon" />
              <span class="nx-rich-text-toolbar__caret" aria-hidden="true" />
            </button>
          </template>
          <div class="nx-rich-text-toolbar__aligns" role="group">
            <NxIconButton
              v-for="[align, icon, key, label, shortcut] in ALIGNS"
              :key="align"
              height="xs"
              :pressed="state.align === align"
              :title="titled(tr(key, label), keys(shortcut))"
              @click="pickAlign(align)"
            >
              <NxOfficeIcon :name="icon" />
            </NxIconButton>
          </div>
        </NxPopover>
        <NxIconButton
          height="xs"
          :disabled="off('list')"
          :pressed="state.list === 'bulletList'"
          :title="tr('bullet_list', 'Bullet list')"
          @click="run({ id: 'list', list: 'bulletList' })"
        >
          <NxOfficeIcon name="bullets" />
        </NxIconButton>
        <NxIconButton
          height="xs"
          :disabled="off('list')"
          :pressed="state.list === 'orderedList'"
          :title="tr('numbered_list', 'Numbered list')"
          @click="run({ id: 'list', list: 'orderedList' })"
        >
          <NxOfficeIcon name="numbering" />
        </NxIconButton>
        <template v-if="full">
          <NxIconButton
            height="xs"
            :disabled="off('indent')"
            :title="titled(tr('decrease_indent', 'Decrease indent'), keys('M', true))"
            @click="run({ id: 'indent', delta: -1 })"
          >
            <NxOfficeIcon name="indent-decrease" />
          </NxIconButton>
          <NxIconButton
            height="xs"
            :disabled="off('indent')"
            :title="titled(tr('increase_indent', 'Increase indent'), keys('M'))"
            @click="run({ id: 'indent', delta: 1 })"
          >
            <NxOfficeIcon name="indent-increase" />
          </NxIconButton>
        </template>
      </div>

      <div v-if="insertInBar" class="nx-rich-text-toolbar__group is-insert">
        <NxPopover v-model:open="barLinkOpen" :label="tr('link', 'Link')" @opened="focusLink">
          <template #trigger>
            <NxIconButton
              height="xs"
              :disabled="off('link')"
              :pressed="!!state.marks.link"
              :title="titled(tr('link', 'Link'), keys('K'))"
            >
              <NxOfficeIcon name="link" />
            </NxIconButton>
          </template>
          <RichTextLinkForm
            ref="barLinkForm"
            v-model="linkHref"
            :url-label="tr('url', 'URL')"
            :apply-label="tr('apply', 'Apply')"
            :remove-label="tr('remove', 'Remove')"
            @apply="applyLink"
            @remove="removeLink"
            @cancel="closeLink"
          />
        </NxPopover>
        <NxIconButton
          v-if="allowImages"
          height="xs"
          :disabled="off('image')"
          :title="tr('image', 'Image')"
          @click="run({ id: 'image' })"
        >
          <NxOfficeIcon name="picture" />
        </NxIconButton>
        <NxPopover
          v-if="blocks"
          v-model:open="barTableOpen"
          :label="tr('insert_table', 'Insert table')"
        >
          <template #trigger>
            <NxIconButton height="xs" :disabled="off('table')" :title="tr('table', 'Table')">
              <NxOfficeIcon name="table" />
            </NxIconButton>
          </template>
          <NxTableSizeGrid :dimensions="tableDimensions" @pick="pickTable" />
        </NxPopover>
        <slot name="insert-actions" />
        <template v-if="full && blocks">
          <NxIconButton
            height="xs"
            :disabled="off('quote')"
            :title="tr('add_quote', 'Quote')"
            @click="run({ id: 'quote', delta: 1 })"
          >
            <NxOfficeIcon name="quote" />
          </NxIconButton>
          <NxIconButton
            height="xs"
            :disabled="off('divider')"
            :title="tr('divider', 'Divider')"
            @click="run({ id: 'divider' })"
          >
            <NxOfficeIcon name="divider" />
          </NxIconButton>
        </template>
      </div>

      <div
        v-if="extensions.length && extensionContext"
        class="nx-rich-text-toolbar__group is-extensions"
        role="group"
        :aria-label="tr('extensions', 'Extensions')"
      >
        <NxRichTextExtensionButtons
          ref="extensionButtons"
          :extensions="extensions"
          :context="extensionContext!"
          @closed="emit('extension-closed')"
        />
      </div>

      <div v-if="inBar('find')" class="nx-rich-text-toolbar__group is-find">
        <NxIconButton
          height="xs"
          :disabled="off('find')"
          :title="titled(tr('find', 'Find'), keys('F'))"
          @click="run({ id: 'find' })"
        >
          <NxOfficeIcon name="find" />
        </NxIconButton>
        <NxIconButton
          height="xs"
          :disabled="off('replace')"
          :title="titled(tr('replace', 'Replace'), keys('H'))"
          @click="run({ id: 'replace' })"
        >
          <NxOfficeIcon name="replace" />
        </NxIconButton>
      </div>

      <div class="nx-rich-text-toolbar__group is-more">
        <NxPopover
          v-model:open="menuShown"
          side="bottom-start"
          :label="tr('more_tools', 'More tools')"
          @opened="onMenuOpened"
        >
          <template #trigger>
            <NxIconButton
              height="xs"
              :pressed="menuShown"
              :title="tr('more_tools', 'More tools')"
              :aria-label="tr('more_tools', 'More tools')"
            >
              <NxOfficeIcon name="more" />
            </NxIconButton>
          </template>
          <div v-if="menuView === 'link'" class="nx-rich-text-toolbar__menu-view">
            <div class="nx-rich-text-toolbar__menu-head">{{ tr('link', 'Link') }}</div>
            <RichTextLinkForm
              ref="menuLinkForm"
              v-model="linkHref"
              :url-label="tr('url', 'URL')"
              :apply-label="tr('apply', 'Apply')"
              :remove-label="tr('remove', 'Remove')"
              @apply="applyLink"
              @remove="removeLink"
              @cancel="closeLink"
            />
          </div>
          <div v-else-if="menuView === 'table'" class="nx-rich-text-toolbar__menu-view">
            <div class="nx-rich-text-toolbar__menu-head">
              {{ tr('insert_table', 'Insert table') }}
            </div>
            <NxTableSizeGrid :dimensions="tableDimensions" @pick="pickTable" />
          </div>
          <div v-else class="nx-rich-text-toolbar__menu-wrap">
            <div class="nx-rich-text-toolbar__menu" role="menu">
              <section
                v-for="section in menuSections"
                :key="section.key"
                class="nx-rich-text-toolbar__menu-section"
                :aria-label="section.label"
              >
                <div class="nx-rich-text-toolbar__menu-head">{{ section.label }}</div>
                <template v-for="entry in section.entries" :key="entry.key">
                  <button
                    v-if="entry.kind === 'action'"
                    type="button"
                    role="menuitem"
                    class="nx-rich-text-toolbar__menu-row is-action"
                    :class="{ 'is-on': entry.pressed }"
                    :disabled="entry.disabled"
                    :aria-pressed="entry.pressed === undefined ? undefined : entry.pressed"
                    :data-entry="entry.key"
                    @click="selectEntry(entry)"
                  >
                    <span class="nx-rich-text-toolbar__menu-icon">
                      <NxOfficeIcon v-if="entry.icon" :name="entry.icon" />
                    </span>
                    <span class="nx-rich-text-toolbar__menu-label">{{ entry.label }}</span>
                    <kbd v-if="entry.shortcut" class="nx-rich-text-toolbar__menu-keys">
                      {{ entry.shortcut }}
                    </kbd>
                  </button>
                  <label
                    v-else-if="entry.kind === 'select'"
                    class="nx-rich-text-toolbar__menu-row is-select"
                    :data-entry="entry.key"
                  >
                    <span class="nx-rich-text-toolbar__menu-icon" />
                    <span class="nx-rich-text-toolbar__menu-label">{{ entry.label }}</span>
                    <span class="nx-rich-text-toolbar__pick is-menu">
                      <select
                        :value="entry.value"
                        :disabled="entry.disabled"
                        :aria-label="entry.label"
                        @change="entry.change(pick($event))"
                      >
                        <option
                          v-for="option in entry.options"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                    </span>
                  </label>
                  <label
                    v-else
                    class="nx-rich-text-toolbar__menu-row is-color"
                    :class="{ 'is-disabled': entry.disabled }"
                    :data-entry="entry.key"
                  >
                    <span class="nx-rich-text-toolbar__menu-icon">
                      <NxOfficeIcon :name="entry.icon" :current="entry.current" />
                    </span>
                    <span class="nx-rich-text-toolbar__menu-label">{{ entry.label }}</span>
                    <input
                      type="color"
                      :value="entry.value"
                      :disabled="entry.disabled"
                      :aria-label="entry.label"
                      @input="entry.change(pick($event))"
                    />
                  </label>
                </template>
              </section>
            </div>
            <button
              v-if="customizable"
              type="button"
              role="menuitem"
              class="nx-rich-text-toolbar__menu-row is-action nx-rich-text-toolbar__menu-foot"
              @click="customize"
            >
              <span class="nx-rich-text-toolbar__menu-icon" />
              <span class="nx-rich-text-toolbar__menu-label">
                {{ tr('customize', 'Customise toolbar…') }}
              </span>
            </button>
          </div>
        </NxPopover>
      </div>

      <span v-if="full" class="nx-rich-text-toolbar__pick is-paste">
        <select
          :value="state.pasteMode"
          :disabled="off('pasteMode')"
          :aria-label="tr('paste_from_word', 'Pasting from Word')"
          :title="tr('paste_from_word', 'Pasting from Word')"
          @change="run({ id: 'pasteMode', mode: pick($event) as NxRichTextPasteMode })"
        >
          <option v-for="option in pasteOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </span>

      <div v-if="$slots.end" class="nx-rich-text-toolbar__end">
        <slot name="end" />
      </div>
      <span v-if="full" class="nx-rich-text-toolbar__break" aria-hidden="true" />
    </div>

    <div
      v-if="state.image"
      class="nx-rich-text-toolbar__context"
      role="group"
      :aria-label="tr('image_tools', 'Image tools')"
    >
      <span class="nx-rich-text-toolbar__context-label">{{ tr('image', 'Image') }}</span>
      <button
        v-for="[wrap, key, label] in IMAGE_WRAPS"
        :key="wrap"
        type="button"
        class="nx-rich-text-toolbar__chip"
        :class="{ 'is-on': state.image.wrap === wrap }"
        :disabled="off('imageWrap')"
        :aria-pressed="state.image.wrap === wrap"
        @click="run({ id: 'imageWrap', wrap })"
      >
        {{ tr(key, label) }}
      </button>
      <button
        type="button"
        class="nx-rich-text-toolbar__chip"
        :disabled="off('imageEdit')"
        @click="run({ id: 'imageEdit' })"
      >
        <NxOfficeIcon name="crop" />
        {{ tr('image_edit', 'Crop, size & description') }}
      </button>
      <button
        type="button"
        class="nx-rich-text-toolbar__chip"
        :disabled="off('imageDelete')"
        @click="run({ id: 'imageDelete' })"
      >
        <NxOfficeIcon name="delete-picture" />
        {{ tr('delete_image', 'Delete picture') }}
      </button>
    </div>
    <div
      v-if="state.inTable"
      class="nx-rich-text-toolbar__context"
      role="group"
      :aria-label="tr('table', 'Table')"
    >
      <span class="nx-rich-text-toolbar__context-label">{{ tr('table', 'Table') }}</span>
      <button
        type="button"
        class="nx-rich-text-toolbar__chip"
        :disabled="off('tableRow')"
        @click="run({ id: 'tableRow', op: 'insert' })"
      >
        <NxOfficeIcon name="insert-row" />
        {{ tr('insert_row', 'Insert row') }}
      </button>
      <button
        type="button"
        class="nx-rich-text-toolbar__chip"
        :disabled="off('tableRow')"
        @click="run({ id: 'tableRow', op: 'delete' })"
      >
        <NxOfficeIcon name="delete-row" />
        {{ tr('delete_row', 'Delete row') }}
      </button>
      <button
        type="button"
        class="nx-rich-text-toolbar__chip"
        :disabled="off('tableColumn')"
        @click="run({ id: 'tableColumn', op: 'insert' })"
      >
        <NxOfficeIcon name="insert-column" />
        {{ tr('insert_column', 'Insert column') }}
      </button>
      <button
        type="button"
        class="nx-rich-text-toolbar__chip"
        :disabled="off('tableColumn')"
        @click="run({ id: 'tableColumn', op: 'delete' })"
      >
        <NxOfficeIcon name="delete-column" />
        {{ tr('delete_column', 'Delete column') }}
      </button>
      <button
        type="button"
        class="nx-rich-text-toolbar__chip"
        :disabled="off('tableMerge')"
        @click="run({ id: 'tableMerge' })"
      >
        {{ tr('merge', 'Merge') }}
      </button>
      <button
        type="button"
        class="nx-rich-text-toolbar__chip"
        :disabled="off('tableSplit')"
        @click="run({ id: 'tableSplit' })"
      >
        {{ tr('split', 'Split') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.nx-rich-text-toolbar {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
  background: var(--nxt-surface-bg);
}

/* One flex row that wraps when narrow; the full layout breaks it in two. */
.nx-rich-text-toolbar__bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  row-gap: var(--nxt-space-2xs);
  padding: var(--nxt-space-2xs) 0;
}

.nx-rich-text-toolbar__group {
  display: flex;
  align-items: center;
  gap: var(--nxt-hairline);
  padding: 0 var(--nxt-space-2xs);
  border-left: var(--nxt-hairline) solid var(--nxt-border);
}

.nx-rich-text-toolbar__group.is-history,
.nx-rich-text-toolbar__group.is-colors {
  border-left: 0;
}

.nx-rich-text-toolbar__group.is-colors {
  padding-left: 0;
}

/* The full layout: row one — history, font, colours, find, the host's tools;
   row two — marks, paragraph, insert, „⋯“ and pasting from Word. */
.nx-rich-text-toolbar--full .is-history {
  order: 1;
}

.nx-rich-text-toolbar--full .is-font {
  order: 2;
}

.nx-rich-text-toolbar--full .is-colors {
  order: 3;
  padding-left: var(--nxt-space-2xs);
  border-left: var(--nxt-hairline) solid var(--nxt-border);
}

.nx-rich-text-toolbar--full .is-find {
  order: 4;
}

.nx-rich-text-toolbar--full .nx-rich-text-toolbar__end {
  order: 5;
}

.nx-rich-text-toolbar--full .nx-rich-text-toolbar__break {
  order: 6;
  flex-basis: 100%;
  height: 0;
  border-top: var(--nxt-hairline) solid var(--nxt-border);
  margin: var(--nxt-space-2xs) calc(var(--nxt-space-xs) * -1);
}

.nx-rich-text-toolbar--full .is-marks {
  order: 7;
  border-left: 0;
}

.nx-rich-text-toolbar--full .is-paragraph {
  order: 8;
}

.nx-rich-text-toolbar--full .is-insert,
.nx-rich-text-toolbar--full .is-extensions {
  order: 9;
}

.nx-rich-text-toolbar--full .is-more {
  order: 10;
}

.nx-rich-text-toolbar--full .is-paste {
  order: 11;
  margin-left: auto;
}

.nx-rich-text-toolbar__end {
  display: flex;
  align-items: center;
  margin-left: auto;
  padding-left: var(--nxt-space-2xs);
  border-left: var(--nxt-hairline) solid var(--nxt-border);
}

/* A select that looks like a ghost button: its value, a caret, no frame. */
.nx-rich-text-toolbar__pick {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.nx-rich-text-toolbar__pick select {
  height: var(--nxt-control-size-sm);
  padding: 0 calc(var(--nxt-space-md) + var(--nxt-space-sm)) 0 var(--nxt-space-xs);
  border: 0;
  border-radius: var(--nxt-radius-sm);
  outline: none;
  appearance: none;
  background: transparent;
  color: var(--nxt-fg);
  font: inherit;
  font-size: var(--nxt-font-size-xs);
  text-overflow: ellipsis;
  cursor: pointer;
}

.nx-rich-text-toolbar__pick select:hover:not(:disabled) {
  background: var(--nxt-surface-sunken-bg);
}

.nx-rich-text-toolbar__pick select:focus-visible {
  box-shadow: 0 0 0 var(--nxt-hairline) var(--nxt-brand-bg);
}

.nx-rich-text-toolbar__pick select:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.nx-rich-text-toolbar__pick::after,
.nx-rich-text-toolbar__caret {
  width: calc(var(--nxt-size-unit) * 0.3125);
  height: calc(var(--nxt-size-unit) * 0.3125);
  border-right: calc(var(--nxt-hairline) * 1.5) solid var(--nxt-fg-muted);
  border-bottom: calc(var(--nxt-hairline) * 1.5) solid var(--nxt-fg-muted);
  transform: translateY(-25%) rotate(45deg);
  pointer-events: none;
}

.nx-rich-text-toolbar__pick::after {
  position: absolute;
  right: var(--nxt-space-sm);
  content: '';
}

.nx-rich-text-toolbar__caret {
  display: inline-block;
  margin-left: var(--nxt-space-2xs);
  transform: translateY(-25%) rotate(45deg) scale(0.8);
}

.nx-rich-text-toolbar__pick.is-heading select {
  width: calc(var(--nxt-size-unit) * 5.5);
}

.nx-rich-text-toolbar__pick.is-family select {
  width: calc(var(--nxt-size-unit) * 5.75);
}

.nx-rich-text-toolbar__pick.is-size select {
  width: calc(var(--nxt-size-unit) * 3);
}

.nx-rich-text-toolbar__pick.is-paste select,
.nx-rich-text-toolbar__pick.is-spacing select {
  color: var(--nxt-fg-muted);
}

/* The colour buttons: the icon shows the colour, the native picker opens on click. */
.nx-rich-text-toolbar__color,
.nx-rich-text-toolbar__split {
  position: relative;
  display: inline-flex;
  align-items: center;
  height: var(--nxt-control-size-sm);
  padding: 0 var(--nxt-space-2xs) 0 var(--nxt-space-xs);
  border: 0;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg);
  cursor: pointer;
}

.nx-rich-text-toolbar__color:hover:not(.is-disabled),
.nx-rich-text-toolbar__split:hover:not(:disabled) {
  background: var(--nxt-surface-sunken-bg);
}

.nx-rich-text-toolbar__color.is-disabled,
.nx-rich-text-toolbar__split:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.nx-rich-text-toolbar__color input,
.nx-rich-text-toolbar__menu-row.is-color input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  opacity: 0;
  cursor: inherit;
}

.nx-rich-text-toolbar__aligns {
  display: flex;
  gap: var(--nxt-hairline);
}

/* „⋯“: two columns of sections, one on a narrow screen. */
.nx-rich-text-toolbar__menu {
  /* The popover's widest panel, less its padding. */
  width: calc(var(--nxt-panel-width-2xl) - var(--nxt-space-md) * 2);
  max-width: calc(90vw - var(--nxt-space-md) * 2);
  max-height: min(70vh, calc(var(--nxt-size-unit) * 30));
  overflow-y: auto;
  column-count: 2;
  column-gap: var(--nxt-space-sm);
  column-rule: var(--nxt-hairline) solid var(--nxt-border);
}

.nx-rich-text-toolbar__menu-section {
  break-inside: avoid;
  padding-bottom: var(--nxt-space-xs);
}

@media (max-width: 640px) {
  .nx-rich-text-toolbar__menu {
    width: calc(var(--nxt-size-unit) * 17);
    column-count: 1;
  }
}

.nx-rich-text-toolbar__menu-head {
  padding: var(--nxt-space-xs) var(--nxt-space-sm) var(--nxt-space-2xs);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.nx-rich-text-toolbar__menu-icon {
  display: inline-flex;
  flex-shrink: 0;
  justify-content: center;
  width: var(--nxt-icon-size-sm);
}

.nx-rich-text-toolbar__menu-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nx-rich-text-toolbar__menu-keys {
  margin-left: var(--nxt-space-xs);
  color: var(--nxt-fg-muted);
  font: inherit;
  font-size: var(--nxt-font-size-xs);
}

.nx-rich-text-toolbar__menu-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  width: 100%;
  min-height: var(--nxt-control-size-sm);
  padding: 0 var(--nxt-space-xs);
  border: 0;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg);
  font: inherit;
  font-size: var(--nxt-font-size-xs);
  text-align: left;
  white-space: nowrap;
}

/* A choice in the menu: its select under the label, the full column wide. */
.nx-rich-text-toolbar__menu-row.is-select {
  flex-wrap: wrap;
  row-gap: 0;
  padding-block: var(--nxt-space-2xs);
}

.nx-rich-text-toolbar__menu-row.is-select .nx-rich-text-toolbar__pick {
  flex-basis: 100%;
  padding-left: calc(var(--nxt-icon-size-sm) + var(--nxt-space-xs));
}

.nx-rich-text-toolbar__menu-row.is-action,
.nx-rich-text-toolbar__menu-row.is-color {
  cursor: pointer;
}

.nx-rich-text-toolbar__menu-row.is-action:hover:not(:disabled),
.nx-rich-text-toolbar__menu-row.is-action:focus-visible,
.nx-rich-text-toolbar__menu-row.is-color:hover:not(.is-disabled) {
  outline: none;
  background: var(--nxt-surface-sunken-bg);
}

.nx-rich-text-toolbar__menu-row.is-on {
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
}

.nx-rich-text-toolbar__menu-row.is-action:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.nx-rich-text-toolbar__menu-row.is-disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.nx-rich-text-toolbar__pick.is-menu select {
  width: 100%;
  height: calc(var(--nxt-control-size-sm) - var(--nxt-space-xs));
  padding-left: 0;
  color: var(--nxt-fg-muted);
}

.nx-rich-text-toolbar__menu-foot {
  margin-top: var(--nxt-space-2xs);
  border-top: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: 0;
  color: var(--nxt-brand-subtle-fg);
}

.nx-rich-text-toolbar__menu-view {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
}

/* A picture or a table under the caret brings its own tools in a second row. */
.nx-rich-text-toolbar__context {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding: var(--nxt-space-2xs) var(--nxt-space-sm);
  border-top: var(--nxt-hairline) solid var(--nxt-border);
  background: var(--nxt-surface-sunken-bg);
}

.nx-rich-text-toolbar__context-label {
  margin-right: var(--nxt-space-xs);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.nx-rich-text-toolbar__chip {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  height: calc(var(--nxt-control-size-sm) - var(--nxt-space-xs));
  padding: 0 var(--nxt-space-sm);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-lg);
  background: var(--nxt-surface-bg);
  color: var(--nxt-fg);
  font: inherit;
  font-size: var(--nxt-font-size-xs);
  cursor: pointer;
}

.nx-rich-text-toolbar__chip:hover:not(:disabled) {
  border-color: var(--nxt-border-strong);
}

.nx-rich-text-toolbar__chip.is-on {
  border-color: var(--nxt-brand-border);
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
}

.nx-rich-text-toolbar__chip:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
